'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

const pastilleToken =
  'data-[leading-icon=true]:[&>svg:first-child]:box-border data-[leading-icon=true]:[&>svg:first-child]:rounded-[0.6rem] data-[leading-icon=true]:[&>svg:first-child]:bg-white/[0.13] data-[leading-icon=true]:[&>svg:first-child]:ring-1 data-[leading-icon=true]:[&>svg:first-child]:ring-white/[0.10] data-[leading-icon=true]:[&>svg:first-child]:transition-[background-color,box-shadow] data-[leading-icon=true]:[&>svg:first-child]:duration-200 data-[leading-icon=true]:hover:[&>svg:first-child]:bg-white/[0.22] data-[leading-icon=true]:hover:[&>svg:first-child]:ring-white/20 data-[leading-icon=true]:motion-reduce:[&>svg:first-child]:transition-none';

export const buttonVariants = cva(
  'ring-offset-background inline-flex items-center justify-center gap-2 rounded-xl border border-transparent tracking-[-0.008em] whitespace-nowrap transition-[background-color,border-color,color,box-shadow] duration-200 ease-out select-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--btn-ink-ring)/0.55)] focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-[0.42] disabled:shadow-none motion-reduce:transition-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-[hsl(var(--btn-ink))] font-[640] text-[hsl(var(--btn-ink-foreground))] shadow-[inset_0_1px_0_hsl(0_0%_100%/0.13),0_1px_2px_hsl(var(--btn-ink-shadow)/0.32),0_6px_16px_-8px_hsl(var(--btn-ink-shadow)/0.55)] hover:bg-[hsl(var(--btn-ink-hover))] hover:shadow-[inset_0_1px_0_hsl(0_0%_100%/0.17),0_1px_2px_hsl(var(--btn-ink-shadow)/0.26),0_10px_26px_-10px_hsl(var(--btn-ink-shadow)/0.7)] active:bg-[hsl(var(--btn-ink-active))] active:shadow-[inset_0_1.5px_3px_hsl(var(--btn-ink-shadow)/0.4)]',
        accent:
          'bg-[hsl(var(--btn-ink-tint))] font-[620] text-[hsl(var(--btn-ink-tint-foreground))] shadow-[inset_0_0_0_1px_hsl(var(--btn-ink)/0.10)] hover:bg-[hsl(var(--btn-ink-tint-hover))]',
        secondary:
          'bg-card border-border-strong text-foreground shadow-[0_1px_1.5px_hsl(var(--foreground)/0.05)] hover:border-[hsl(var(--btn-ink)/0.28)] hover:text-[hsl(var(--btn-ink))] hover:shadow-[0_2px_8px_-3px_hsl(var(--btn-ink-shadow)/0.22)]',
        outline:
          'bg-card border-border-strong text-foreground shadow-[0_1px_1.5px_hsl(var(--foreground)/0.05)] hover:border-[hsl(var(--btn-ink)/0.28)] hover:text-[hsl(var(--btn-ink))] hover:shadow-[0_2px_8px_-3px_hsl(var(--btn-ink-shadow)/0.22)]',
        ghost:
          'text-foreground-soft hover:bg-[hsl(var(--btn-ink-tint))] hover:text-[hsl(var(--btn-ink-tint-foreground))]',
        destructive:
          'bg-card border-destructive-border text-destructive hover:bg-destructive-tint hover:border-destructive-border-hover',
        link: 'h-auto rounded-none px-0 text-[hsl(var(--btn-ink-tint-foreground))] underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-7 gap-1.5 px-2.5 text-[11.5px] font-[590] [&_svg]:size-3',
        sm: 'h-8 gap-1.5 px-3.25 text-xs font-[590] [&_svg]:size-3.25',
        md: 'h-9.5 gap-2 px-4 text-[13px] font-[590] [&_svg]:size-3.75',
        lg: 'h-11.5 gap-2.5 px-5.5 text-[14.5px] font-[590] [&_svg]:size-4.25',
        icon: 'size-9.5 rounded-[0.7rem] p-0 [&_svg]:size-3.75',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        size: 'xs',
        class: `data-[leading-icon=true]:h-7 data-[leading-icon=true]:gap-1.5 data-[leading-icon=true]:pr-3 data-[leading-icon=true]:pl-0.75 ${pastilleToken} data-[leading-icon=true]:[&>svg:first-child]:size-5.5 data-[leading-icon=true]:[&>svg:first-child]:p-1.25`,
      },
      {
        variant: 'primary',
        size: 'sm',
        class: `data-[leading-icon=true]:h-8 data-[leading-icon=true]:gap-2 data-[leading-icon=true]:pr-3.75 data-[leading-icon=true]:pl-1 ${pastilleToken} data-[leading-icon=true]:[&>svg:first-child]:size-6 data-[leading-icon=true]:[&>svg:first-child]:p-1.5`,
      },
      {
        variant: 'primary',
        size: 'md',
        class: `data-[leading-icon=true]:h-10 data-[leading-icon=true]:gap-2.75 data-[leading-icon=true]:pr-5 data-[leading-icon=true]:pl-1.25 ${pastilleToken} data-[leading-icon=true]:[&>svg:first-child]:size-7.5 data-[leading-icon=true]:[&>svg:first-child]:p-2`,
      },
      {
        variant: 'primary',
        size: 'lg',
        class: `data-[leading-icon=true]:h-12.5 data-[leading-icon=true]:gap-3 data-[leading-icon=true]:pr-6.5 data-[leading-icon=true]:pl-1.5 ${pastilleToken} data-[leading-icon=true]:[&>svg:first-child]:size-9.5 data-[leading-icon=true]:[&>svg:first-child]:p-2.75`,
      },
      { variant: 'primary', size: 'icon', class: 'size-9.5' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function hasLeadingIcon(children: React.ReactNode): boolean {
  const [first] = React.Children.toArray(children).filter((child) => child !== '');
  return React.isValidElement(first);
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedType = asChild ? undefined : (type ?? 'button');
    const leadingIcon = !asChild && hasLeadingIcon(children);
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        type={resolvedType}
        data-leading-icon={leadingIcon ? 'true' : undefined}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export const ButtonSplit = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'inline-flex items-stretch [&>*:last-child]:ml-px [&>*:last-child]:px-0',
        className,
      )}
      {...props}
    />
  ),
);
ButtonSplit.displayName = 'ButtonSplit';
